import {
  type AppSseEvent,
  type PatientSyncJobMetadataProgress,
  type PatientSyncJobPhase,
  type PatientSyncJobStatus,
} from "@elation/api-client/src/stitch-web";
import { SERVICE_URL } from "@src/env";
import { useEffect } from "react";
import { appBus } from "../../events";
import { useSSE } from "./useSSE";

export interface AppSSEEventsOptions {
  enabled?: boolean;
}

/**
 * Live patient sync job state derived from the app SSE stream.
 *
 * We own this in component state instead of the React Query cache because
 * `useAppSSEEvents` is the single source of truth for it.
 */
export interface PatientSyncJobState {
  jobId: string;
  phase: PatientSyncJobPhase;
  status: PatientSyncJobStatus;
  progress: PatientSyncJobMetadataProgress;
  error?: string;
}

type AppSSEEventMap = {
  [K in AppSseEvent["type"]]: Extract<AppSseEvent, { type: K }>;
};

/**
 * Subscribes to the authenticated actor's single multiplexed SSE stream and
 * exposes the derived state every consumer in the app needs:
 *
 * - **Patient sync job** progress / completion / failure (used by the patient
 *   list page).
 * - **Active encounter** lifecycle (`finalizing` / `completed` / `reopened` /
 *   `superseded_by_reopen`) and **agenda item** CRUD for the optional
 *   `encounterId`. Encounter events for other ids on the same stream are
 *   filtered out so multiple encounter views don't cross-talk.
 *
 * The server exposes one channel per session (`GET /api/events`, credentials
 * identify the physician or staff). Browsers cap concurrent same-origin
 * connections (HTTP/1.1: 6) and SSE connections are long-lived, so the app
 * should open at most one stream and multiplex here and on the server.
 *
 * Encounter events are also re-emitted on the local `appBus` so
 * components that don't render under PatientLayout (e.g. the Transcript
 * panel) can subscribe without adding their own SSE connection.
 *
 * If the SSE connection drops past the fallback threshold, the hook
 * transparently switches to polling `/api/jobs/:id` until the patient sync
 * job reaches a terminal state (`completed` / `failed`) or SSE reconnects —
 * so the UI never gets stuck on a stale "running" status.
 *
 * @param physicianId When `null`, the hook does not open a connection (e.g.
 *   physician context not ready yet). Must match the signed-in physician when
 *   set; used to reset stream state when switching users.
 */
export function useAppSSEEvents(
  userId: number | null,
  type: "physician" | "staff",
  options?: AppSSEEventsOptions,
) {
  const { enabled = true } = options ?? {};

  const queryKey =
    userId != null
      ? (["/api/events", userId] as const)
      : (["app-sse-disabled"] as const);

  const { isFallbackPolling } = useSSE<AppSSEEventMap>({
    url:
      type === "physician"
        ? `${SERVICE_URL}/api/events?physicianId=${userId}`
        : `${SERVICE_URL}/api/events?staffId=${userId}`,
    queryKey,
    enabled: enabled && userId != null,
    handlers: {
      "physician.patient_sync_started"(payload) {
        appBus.emit("physician.patient_sync_started", payload);
      },
      "physician.patient_sync_progress"(payload) {
        appBus.emit("physician.patient_sync_progress", payload);
      },
      "physician.patient_sync_succeeded"(payload) {
        appBus.emit("physician.patient_sync_succeeded", payload);
      },
      "physician.patient_sync_failed"(payload) {
        appBus.emit("physician.patient_sync_failed", payload);
      },
      "encounter.snapshot"(payload) {
        appBus.emit("encounter.snapshot", payload);
      },
      "agenda_item.created"(payload) {
        appBus.emit("agenda_item.created", payload);
      },
      "agenda_item.updated"(payload) {
        appBus.emit("agenda_item.updated", payload);
      },
      "agenda_item.deleted"(payload) {
        appBus.emit("agenda_item.deleted", payload);
      },
      "encounter.finalizing"(payload) {
        appBus.emit("encounter.finalizing", payload);
      },
      "encounter.completed"(payload) {
        appBus.emit("encounter.completed", payload);
      },
      "encounter.reopened"(payload) {
        appBus.emit("encounter.reopened", payload);
      },
      "encounter.superseded_by_reopen"(payload) {
        appBus.emit("encounter.superseded_by_reopen", payload);
      },
      "encounter.transcription_created"(payload) {
        appBus.emit("encounter.transcription_created", payload);
      },
      "transcript.snapshot"(data) {
        appBus.emit("transcript.snapshot", data);
      },
      "transcript.item.created"(data) {
        appBus.emit("transcript.item.created", data);
      },
    },
  });

  useEffect(() => {
    appBus.emit("sse.fallback-polling", { isFallbackPolling });
  }, [isFallbackPolling]);
}
